import { portalText } from "../../../i18n/portal.js";
/**
 * The purchase terms a plan purchase needs — plan-014 GAP (E4's hand-off: "a console read of the current purchase
 * terms revision so 'Confirm and pay' can pass `terms_revision`"; PD-8, design §27.8.15). Over B1's
 * `acceptAccountTerms` and the read beside it, `readAccountTerms` (`GET /v1/accounts/{id}/terms-acceptance`).
 *
 * OPTIONAL ON THE GATEWAY, like the operator-action confirmation: merged onto `ConsoleGateway` here and the API
 * binding spread into `apiGateway`. The sandbox takes no real payment and asks for no terms, so it carries neither,
 * and the Plans page confirms there without a revision (the sandbox's confirmation ignores it).
 *
 * Until the api-client lock holder regenerates the client, `readAccountTerms` is read through a structural view: a
 * client that lacks it raises naming the regeneration — never an empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import { readBoolean, readRecord, readText } from "../../../api/decode.js";

/** The purchase terms revision in force for an organization. */
export interface PurchaseTermsView {
  readonly revision: string;
  readonly termsUrl: string;
  readonly privacyUrl: string;
  /** The organization accepted this revision. */
  readonly accepted: boolean;
  /** The signed-in person may accept it (an Owner or a Finance member). */
  readonly canAccept: boolean;
}

declare module "../../../api/gateway.js" {
  interface ConsoleGateway {
    /** The purchase terms in force and whether the organization accepted them. */
    readonly readPurchaseTerms?: (accountId: string) => Promise<PurchaseTermsView>;
    /** An Owner or Finance member accepts `revision` for the organization's purchases. */
    readonly acceptPurchaseTerms?: (accountId: string, revision: string) => Promise<void>;
  }
}

function readTermsOperation(client: WeTalkApiClient): (request: { path: { account_id: string } }) => Promise<ApiResult> {
  const held = (client as unknown as Partial<Record<"readAccountTerms", unknown>>)["readAccountTerms"];
  return (request) => {
    if (typeof held !== "function") {
      throw new Error(portalText("finance", "m0717"));
    }
    return (held as (request: { path: { account_id: string } }) => Promise<ApiResult>).call(client, request);
  };
}

/** The wire answer, read by name; anything else is a contract fault. */
export function purchaseTermsOf(body: unknown): PurchaseTermsView {
  const data = readRecord(readRecord(body, "readAccountTerms")["data"], "readAccountTerms.data");
  return {
    revision: readText(data["revision"], "readAccountTerms.data.revision"),
    termsUrl: readText(data["terms_url"], "readAccountTerms.data.terms_url"),
    privacyUrl: readText(data["privacy_url"], "readAccountTerms.data.privacy_url"),
    accepted: readBoolean(data["accepted"], "readAccountTerms.data.accepted"),
    canAccept: readBoolean(data["can_accept"], "readAccountTerms.data.can_accept"),
  };
}

/** The API binding, spread into `apiGateway` beside the other slices. */
export function purchaseTermsApiSlice(client: WeTalkApiClient): {
  readonly readPurchaseTerms: (accountId: string) => Promise<PurchaseTermsView>;
  readonly acceptPurchaseTerms: (accountId: string, revision: string) => Promise<void>;
} {
  const readTerms = readTermsOperation(client);
  return {
    async readPurchaseTerms(accountId) {
      return purchaseTermsOf(assertOk(await readTerms({ path: { account_id: accountId } })));
    },
    async acceptPurchaseTerms(accountId, revision) {
      assertOk(await client.acceptAccountTerms({ path: { account_id: accountId }, body: { revision } }));
    },
  };
}
